import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Field, Form } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useFeatures } from '~/hooks';
import revenueAttributionMethods from '~/lookups/revenue-attribution-methods';
import revenueRecognitionMethods from '~/lookups/revenue-recognition-methods';
import SettingsFormSection from '../../SettingsFormSection';
import ProjectPurchaseOrderSelect from '../ProjectPurchaseOrderSelect';

export default function Billing({ project, formik }) {
  const api = useApi();
  const features = useFeatures();
  const { workspace } = useWorkspace();

  const { client, isBillable, billingTypeId } = formik.values;
  const locked = project.hasInvoices || project.hasCreditNotes;

  const [clientGraph, setClientGraph] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await api.www.workspaces(workspace.id).projects(project.id).client();
      setClientGraph(data);
    })();
  }, [client.id, project.id, api, workspace.id]);

  // The initial project graph does not include client permissions. So, we need to get them from the client graph. If the client changes, the selector already returns the permissions.
  const clientPermissions = client.permissions ?? clientGraph?.permissions;

  const handleCurrencyChange = ({ target: { value } }) => {
    // Only reset the purchase order if the currency actually changed
    if (value !== formik.values.currency) {
      formik.setValues({ ...formik.values, currency: value, purchaseOrder: null });
    }
  };

  const handleBillingTypeChange = ({ target: { value } }) => {
    const values = { ...formik.values, billingTypeId: value };
    values.isBillable = value !== 'non_billable';

    // For fixed fee projects, an overall budget is required to turn on the monthly budget
    if (values.billingTypeId === 'fixed' && !values.useBudget && values.useMonthlyBudget) {
      values.useMonthlyBudget = false;
    }

    if (values.billingTypeId !== 'tm') values.capRevenue = false;

    formik.setValues(values);
  };

  const handleRevenueRecognitionMethodChange = (event) => {
    const values = { ...formik.values, revenueRecognitionMethod: event.target.value };

    switch (event.target.value) {
      case 'automated_hours':
        values.useBudget = true;
        break;

      case 'automated_tm_basis':
        values.useBudget = true;
        if (values.budgetMode === '') values.budgetMode = 'detailed';
        break;

      case 'automated_services_cost':
        values.useBudget = true;
        break;
    }

    formik.setValues(values);
  };

  return (
    <SettingsFormSection title="Billing">
      <Form.Control>
        <Field.WorkspaceCurrencySelect
          name="currency"
          clearable={false}
          onChange={handleCurrencyChange}
          disabled={
            locked || (!features.multicurrency && project.currency === workspace.currency) || project.hasOpportunities
          }
        />
      </Form.Control>

      <Form.Control>
        <Field.ProjectBillingTypeSelect
          name="billingTypeId"
          placeholder="Billing Type"
          disabled={locked || client?.isInternal}
          onChange={handleBillingTypeChange}
        />
      </Form.Control>

      {isBillable && (
        <>
          {['fixed', 'fixed_recurring'].includes(billingTypeId) && (
            <>
              <Form.Control
                help={
                  <>
                    Services revenue on a fixed fee project can be recognized as of the issue date of the invoice or via
                    a revenue recognition ledger in the Accounting section of the Project Settings tab. For more
                    information, see the{' '}
                    <a
                      href="https://help.ruddr.io/hc/en-us/articles/4410545740055-Fixed-Fee-Revenue-Recognition"
                      target="_blank"
                      rel="noopener noreferrer">
                      Fixed Fee Revenue Recognition
                    </a>{' '}
                    help center article.
                  </>
                }>
                <Field.SingleSelect
                  placeholder="Revenue Recognition Method"
                  name="revenueRecognitionMethod"
                  disabled={!features.manualRevenueRecognition}
                  onChange={handleRevenueRecognitionMethodChange}>
                  {_.map(revenueRecognitionMethods, (option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Field.SingleSelect>
              </Form.Control>

              <Form.Control help={<>How recognized revenue will be attributed to project team members.</>}>
                <Field.SingleSelect placeholder="Revenue Attribution Method" name="revenueAttributionMethod">
                  {_.map(revenueAttributionMethods, (option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </Field.SingleSelect>
              </Form.Control>
            </>
          )}

          {!formik.values.purchaseOrder && (
            <Form.Control>
              <Field.Text name="poNumber" placeholder="PO Number" maxLength={255} />
            </Form.Control>
          )}

          {features.purchaseOrders && (
            <Form.Control>
              <ProjectPurchaseOrderSelect
                name="purchaseOrder"
                project={project}
                clientId={formik.values.client?.id}
                currency={formik.values.currency}
                value={formik.values.purchaseOrder}
                initialValue={project.purchaseOrder}
                onChange={({ target: { value } }) => formik.setFieldValue('purchaseOrder', value)}
                disabled={!clientPermissions?.manageDraftInvoices}
                clearable={clientPermissions?.manageDraftInvoices}
              />
            </Form.Control>
          )}
        </>
      )}
      <Form.Control help="These internal notes will also be shown at the top of the Accounting section of this tab.">
        <Field.TextArea name="accountingNotes" placeholder="Accounting Notes" maxLength={5000} rows={4} />
      </Form.Control>
    </SettingsFormSection>
  );
}
